import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LayoutDashboard,
  ArrowLeftRight,
  PiggyBank,
  Target,
  CalendarClock,
  FolderTree,
  Settings,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  ChevronDown,
  Wallet,
  BarChart3,
  User,
  Upload,
  Command,
  Search,
  ShieldCheck,
  Home as HomeIcon,
  UserCircle,
  Users as UsersIcon,
  Bot,
  TrendingUp,
  GitBranch,
  FlaskConical,
  Briefcase,
} from 'lucide-react';
import CommandPalette from './CommandPalette';

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/transactions', label: 'Transazioni', icon: ArrowLeftRight },
  { path: '/budget', label: 'Budget', icon: PiggyBank },
  { path: '/goals', label: 'Obiettivi', icon: Target },
  { path: '/recurring', label: 'Movimenti ricorrenti', icon: CalendarClock },
  { path: '/categories', label: 'Categorie', icon: FolderTree },
  { path: '/annual', label: 'Report Annuale', icon: BarChart3 },
  { path: '/import', label: 'Importa', icon: Upload },
];

const personalNavItems = [
  { path: '/family', label: 'La mia famiglia', icon: UsersIcon },
  { path: '/by-member', label: 'Per persona', icon: UsersIcon },
  { path: '/me', label: 'Area personale', icon: UserCircle },
];

const investmentsNavItems = [
  { path: '/portfolios', label: 'I miei portafogli', icon: Briefcase },
];

const adminNavItem = { path: '/admin', label: 'Admin', icon: ShieldCheck };

const Layout = ({ children }) => {
  const { user, logout, familyCode } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-card border-r transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <Wallet className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-bold text-lg">Budget Pro</span>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            className="md:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `
                flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                ${isActive
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
              `}
              data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
          <div className="my-2 border-t" />
          <p className="px-3 text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Tu &amp; famiglia</p>
          {personalNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `
                flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                ${isActive
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
              `}
              data-testid={`nav-${item.path.replace('/', '')}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}

          <div className="my-2 border-t" />
          <p className="px-3 text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Investimenti</p>
          {investmentsNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `
                flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                ${isActive
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
              `}
              data-testid={`nav-${item.path.replace('/', '')}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}

          {user?.role === 'admin' && (
            <>
              <div className="my-2 border-t" />
              <p className="px-3 text-[10px] uppercase tracking-wider text-primary/70 font-bold mb-1">Admin</p>
              <NavLink
                to="/admin"
                end
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                  ${isActive
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-primary/80 hover:bg-primary/5 hover:text-primary'}
                `}
                data-testid="nav-admin"
              >
                <adminNavItem.icon className="w-5 h-5" />
                <span>Dashboard</span>
              </NavLink>
              <NavLink
                to="/admin/families"
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                  ${isActive
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-primary/80 hover:bg-primary/5 hover:text-primary'}
                `}
                data-testid="nav-admin-families"
              >
                <HomeIcon className="w-5 h-5" />
                <span>Famiglie</span>
              </NavLink>
              <div className="my-2 border-t border-primary/10" />
              <p className="px-3 text-[10px] uppercase tracking-wider text-primary/70 font-bold mb-1">AI Studio</p>
              <NavLink
                to="/admin/ai-providers"
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                  ${isActive
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-primary/80 hover:bg-primary/5 hover:text-primary'}
                `}
                data-testid="nav-admin-ai-providers"
              >
                <Bot className="w-5 h-5" />
                <span>Provider AI</span>
              </NavLink>
              <NavLink
                to="/admin/market-providers"
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                  ${isActive
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-primary/80 hover:bg-primary/5 hover:text-primary'}
                `}
                data-testid="nav-admin-market-providers"
              >
                <TrendingUp className="w-5 h-5" />
                <span>Provider market</span>
              </NavLink>
              <NavLink
                to="/admin/quant"
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                  ${isActive
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-primary/80 hover:bg-primary/5 hover:text-primary'}
                `}
                data-testid="nav-admin-quant"
              >
                <GitBranch className="w-5 h-5" />
                <span>Quant Lab</span>
              </NavLink>
              <NavLink
                to="/admin/quant/lab"
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                  ${isActive
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-primary/80 hover:bg-primary/5 hover:text-primary'}
                `}
                data-testid="nav-admin-quant-lab"
              >
                <FlaskConical className="w-5 h-5" />
                <span>Quant Workbench</span>
              </NavLink>
            </>
          )}
        </nav>

        {/* Bottom section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <div className="text-xs text-muted-foreground mb-2">
            Codice Famiglia: <span className="font-mono font-medium">{familyCode}</span>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="md:pl-64">
        {/* Top Header */}
        <header className="h-16 border-b bg-card/50 backdrop-blur-sm sticky top-0 z-30">
          <div className="h-full flex items-center justify-between px-4">
            {/* Mobile menu button */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="md:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>

            {/* Search trigger desktop */}
            <button
              onClick={() => {
                window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
              }}
              className="hidden md:flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground bg-muted/40 hover:bg-muted/70 border rounded-lg w-72 transition-colors"
              data-testid="open-command-palette-btn"
            >
              <Search className="w-4 h-4" />
              <span className="flex-1 text-left">Cerca o esegui un'azione…</span>
              <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] rounded border bg-background">
                <Command className="w-3 h-3" />K
              </kbd>
            </button>

            {/* Right side */}
            <div className="flex items-center gap-2">
              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle-btn"
              >
                {theme === 'light' ? (
                  <Moon className="w-5 h-5" />
                ) : (
                  <Sun className="w-5 h-5" />
                )}
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="user-menu-btn">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <span className="hidden sm:inline">{user?.name}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                    <Settings className="w-4 h-4 mr-2" />
                    Impostazioni
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-menu-item">
                    <LogOut className="w-4 h-4 mr-2" />
                    Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-6 lg:p-8 pb-24 md:pb-8">
          {children}
        </main>
      </div>

      {/* Mobile bottom navigation */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-card/95 backdrop-blur-lg border-t">
        <div className="grid grid-cols-5 h-16">
          {[
            { path: '/', icon: LayoutDashboard, label: 'Home' },
            { path: '/transactions', icon: ArrowLeftRight, label: 'Trans' },
            { path: '/budget', icon: PiggyBank, label: 'Budget' },
            { path: '/goals', icon: Target, label: 'Obiett' },
            { path: '/settings', icon: Settings, label: 'Più' },
          ].map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors ${
                  isActive ? 'text-primary' : 'text-muted-foreground'
                }`
              }
              data-testid={`bottom-nav-${item.path.replace('/', '') || 'dashboard'}`}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.75} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Command palette ⌘K */}
      <CommandPalette />
    </div>
  );
};

export default Layout;
